/* =============================================================================
 * Copyright (c) 2026 SKYXOPS Corp. All rights reserved.
 * ============================================================================= */
/**
 * i18n catalog scaffold per swa #56.
 *
 * v0.1 ships English-only. The framework is wired upfront so v2 i18n drops in
 * via `fr.json`, `de.json` etc. without a code refactor:
 *   1. Drop `src/locales/{locale}.json` next to en.json with the same keys.
 *   2. Add the new locale to LOCALES below.
 *   3. The locale resolver picks navigator.language → exact match → prefix
 *      match → en-US fallback.
 *
 * Reviewer rule (swa #56): no raw English strings in JSX. Add a key to en.json
 * and use `<FormattedMessage id=... />` or `useIntl().formatMessage({id:...})`.
 */
import enMessages from './locales/en.json';

export type Messages = Record<string, string>;

export const LOCALES: Record<string, Messages> = {
  'en-US': enMessages as Messages,
};

export const DEFAULT_LOCALE = 'en-US';

export function resolveLocale(): string {
  const candidates: string[] = [];
  if (typeof navigator !== 'undefined') {
    if (navigator.language) candidates.push(navigator.language);
    if (Array.isArray(navigator.languages)) candidates.push(...navigator.languages);
  }
  for (const cand of candidates) {
    if (LOCALES[cand]) return cand;
    const prefix = cand.split('-')[0];
    const hit = Object.keys(LOCALES).find((l) => l.startsWith(`${prefix}-`));
    if (hit) return hit;
  }
  return DEFAULT_LOCALE;
}

export function messagesFor(locale: string): Messages {
  return LOCALES[locale] ?? LOCALES[DEFAULT_LOCALE];
}
